import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Page,
  Layout,
  Card,
  Button,
  TextField,
  Text,
  Select,
  RadioButton,
  BlockStack,
  InlineStack,
  Spinner,
} from '@shopify/polaris'
import { workflowApi, storeApi } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import toast from 'react-hot-toast'
import type {
  WorkflowTrigger,
  WorkflowCondition,
  WorkflowAction,
  ConditionOperator,
  ActionType,
} from '../types'

const TRIGGERS: { value: WorkflowTrigger; label: string; description: string }[] = [
  { value: 'MESSAGE_RECEIVED', label: 'Message Received', description: 'When a customer sends a message' },
  { value: 'ORDER_CREATED', label: 'Order Created', description: 'When a new order is placed' },
  { value: 'ORDER_SHIPPED', label: 'Order Shipped', description: 'When an order is shipped' },
  { value: 'CART_ABANDONED', label: 'Cart Abandoned', description: 'When a cart is left without checkout' },
  { value: 'SENTIMENT_NEGATIVE', label: 'Negative Sentiment', description: 'When negative sentiment is detected' },
  { value: 'CUSTOMER_REGISTERED', label: 'Customer Registered', description: 'When a new customer registers' },
  { value: 'CONVERSATION_OPENED', label: 'Conversation Opened', description: 'When a new conversation starts' },
  { value: 'TIME_BASED', label: 'Time Based', description: 'Scheduled at specific times' },
]

const OPERATORS: { value: ConditionOperator; label: string }[] = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'greater_than', label: 'is greater than' },
  { value: 'less_than', label: 'is less than' },
  { value: 'in', label: 'is in' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
]

const ACTION_TYPES: { value: ActionType; label: string; description: string }[] = [
  { value: 'SEND_MESSAGE', label: 'Send Message', description: 'Send a text message' },
  { value: 'SEND_TEMPLATE', label: 'Send Template', description: 'Send a WhatsApp template' },
  { value: 'ASSIGN_AGENT', label: 'Assign Agent', description: 'Assign conversation to an agent' },
  { value: 'ADD_TAG', label: 'Add Tag', description: 'Add a tag to customer' },
  { value: 'REMOVE_TAG', label: 'Remove Tag', description: 'Remove a tag from customer' },
  { value: 'CLOSE_CONVERSATION', label: 'Close Conversation', description: 'Close the conversation' },
  { value: 'NOTIFY_AGENT', label: 'Notify Agent', description: 'Send notification to an agent' },
  { value: 'WAIT', label: 'Wait', description: 'Wait for a duration' },
]

const CONDITION_FIELDS = [
  { value: 'sentiment', label: 'Sentiment', type: 'select', options: ['positive', 'neutral', 'negative'] },
  { value: 'category', label: 'Category', type: 'select', options: ['SUPPORT', 'SALES', 'COMPLAINT', 'INQUIRY'] },
  { value: 'customer.tags', label: 'Customer Tags', type: 'text' },
  { value: 'customer.name', label: 'Customer Name', type: 'text' },
  { value: 'orderValue', label: 'Order Value', type: 'number' },
  { value: 'cartValue', label: 'Cart Value', type: 'number' },
]

export default function WorkflowBuilderPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [agents, setAgents] = useState<any[]>([])
  const [templates, setTemplates] = useState<any[]>([])

  // Workflow state
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [trigger, setTrigger] = useState<WorkflowTrigger>('MESSAGE_RECEIVED')
  const [conditions, setConditions] = useState<WorkflowCondition[]>([])
  const [actions, setActions] = useState<WorkflowAction[]>([])
  const [priority, setPriority] = useState(0)

  useEffect(() => {
    if (user?.storeId) {
      fetchAgents()
      fetchTemplates()
    }
    if (id && id !== 'new') {
      fetchWorkflow()
    }
  }, [id, user?.storeId])

  const fetchAgents = async () => {
    if (!user?.storeId) return
    try {
      const response = await storeApi.getAgents(user.storeId)
      setAgents(response.data.data.agents)
    } catch (error) {
      console.error('Failed to fetch agents:', error)
    }
  }

  const fetchTemplates = async () => {
    // TODO: Implement template fetching
    setTemplates([])
  }

  const fetchWorkflow = async () => {
    if (!id || id === 'new') return

    setLoading(true)
    try {
      const response = await workflowApi.getWorkflow(id)
      const workflow = response.data.data.workflow
      setName(workflow.name)
      setDescription(workflow.description || '')
      setTrigger(workflow.trigger)
      setConditions(workflow.conditions || [])
      setActions(workflow.actions || [])
      setPriority(workflow.priority || 0)
    } catch (error: any) {
      console.error('Failed to fetch workflow:', error)
      toast.error('Failed to load workflow')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a workflow name')
      return
    }

    if (actions.length === 0) {
      toast.error('Please add at least one action')
      return
    }

    setLoading(true)
    try {
      const data = {
        storeId: user?.storeId,
        name,
        description,
        trigger,
        conditions,
        actions,
        priority,
      }

      if (id && id !== 'new') {
        await workflowApi.updateWorkflow(id, data)
        toast.success('Workflow updated successfully')
      } else {
        await workflowApi.createWorkflow(data)
        toast.success('Workflow created successfully')
      }

      navigate('/workflows')
    } catch (error: any) {
      console.error('Failed to save workflow:', error)
      toast.error(error.response?.data?.error?.message || 'Failed to save workflow')
    } finally {
      setLoading(false)
    }
  }

  const addCondition = () => {
    setConditions([
      ...conditions,
      { field: 'sentiment', operator: 'equals', value: '' },
    ])
  }

  const updateCondition = (index: number, updates: Partial<WorkflowCondition>) => {
    const newConditions = [...conditions]
    newConditions[index] = { ...newConditions[index], ...updates }
    setConditions(newConditions)
  }

  const removeCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index))
  }

  const addAction = (type: ActionType) => {
    let newAction: WorkflowAction = { type }

    switch (type) {
      case 'SEND_MESSAGE':
        newAction = { type, message: '', customerId: '' }
        break
      case 'SEND_TEMPLATE':
        newAction = { type, templateId: '', customerId: '' }
        break
      case 'ASSIGN_AGENT':
        newAction = { type, agentId: '' }
        break
      case 'ADD_TAG':
      case 'REMOVE_TAG':
        newAction = { type, tag: '', customerId: '' }
        break
      case 'NOTIFY_AGENT':
        newAction = { type, agentId: '', message: '' }
        break
      case 'WAIT':
        newAction = { type, duration: 60 }
        break
      default:
        newAction = { type }
    }

    setActions([...actions, newAction])
  }

  const updateAction = (index: number, updates: Partial<WorkflowAction>) => {
    const newActions = [...actions]
    newActions[index] = { ...newActions[index], ...updates }
    setActions(newActions)
  }

  const removeAction = (index: number) => {
    setActions(actions.filter((_, i) => i !== index))
  }

  const moveAction = (index: number, direction: 'up' | 'down') => {
    const newActions = [...actions]
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= actions.length) return
    ;[newActions[index], newActions[newIndex]] = [newActions[newIndex], newActions[index]]
    setActions(newActions)
  }

  if (loading && id !== 'new') {
    return (
      <Page>
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <Spinner size="large" />
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    )
  }

  return (
    <Page
      title={id && id !== 'new' ? 'Edit Workflow' : 'Create Workflow'}
      subtitle="Design an automated workflow with triggers, conditions, and actions"
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: () => navigate('/workflows'),
        },
      ]}
    >
      <Layout>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h3">Basic Information</Text>
              
              <TextField
                label="Name"
                value={name}
                onChange={setName}
                placeholder="e.g., Send welcome message to new customers"
                autoComplete="off"
              />

              <TextField
                label="Description"
                value={description}
                onChange={setDescription}
                placeholder="Optional description"
                multiline={2}
                autoComplete="off"
              />

              <TextField
                label="Priority (0-100)"
                value={priority.toString()}
                onChange={(value) => setPriority(parseInt(value) || 0)}
                type="number"
                min={0}
                max={100}
                helpText="Higher priority workflows execute first"
                autoComplete="off"
              />
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h3">
                <InlineStack gap="200" blockAlign="center">
                  <span style={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: '32px',
                    width: '32px',
                    borderRadius: '50%',
                    background: '#e0f2fe',
                    color: '#0284c7',
                    fontWeight: 'bold'
                  }}>1</span>
                  When (Trigger)
                </InlineStack>
              </Text>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                {TRIGGERS.map((t) => (
                  <div
                    key={t.value}
                    onClick={() => setTrigger(t.value)}
                    style={{
                      padding: '1rem',
                      border: trigger === t.value ? '2px solid #0284c7' : '2px solid #e5e7eb',
                      borderRadius: '8px',
                      background: trigger === t.value ? '#e0f2fe' : 'white',
                      cursor: 'pointer',
                    }}
                  >
                    <RadioButton
                      label={t.label}
                      checked={trigger === t.value}
                      id={t.value}
                      onChange={() => setTrigger(t.value)}
                    />
                    <Text variant="bodySm" as="p" tone="subdued">{t.description}</Text>
                  </div>
                ))}
              </div>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack gap="400" blockAlign="center" align="space-between">
                <Text variant="headingMd" as="h3">
                  <InlineStack gap="200" blockAlign="center">
                    <span style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      height: '32px',
                      width: '32px',
                      borderRadius: '50%',
                      background: '#fef3c7',
                      color: '#d97706',
                      fontWeight: 'bold'
                    }}>2</span>
                    If (Conditions)
                  </InlineStack>
                </Text>
                <Button onClick={addCondition}>Add Condition</Button>
              </InlineStack>

              {conditions.length === 0 ? (
                <Text variant="bodySm" as="p" tone="subdued" alignment="center">
                  No conditions - workflow will always execute when triggered
                </Text>
              ) : (
                <BlockStack gap="300">
                  {conditions.map((condition, index) => (
                    <div key={index} style={{ padding: '1rem', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                      <InlineStack gap="200" align="center">
                        <Text variant="bodySm" as="span" fontWeight="bold">If</Text>

                        <Select
                          label="Field"
                          value={condition.field}
                          onChange={(value) => updateCondition(index, { field: value })}
                          options={CONDITION_FIELDS.map((field) => ({ label: field.label, value: field.value }))}
                        />

                        <Select
                          label="Operator"
                          value={condition.operator}
                          onChange={(value) => updateCondition(index, { operator: value as ConditionOperator })}
                          options={OPERATORS.map((op) => ({ label: op.label, value: op.value }))}
                        />

                        {!['is_empty', 'is_not_empty'].includes(condition.operator) && (
                          <TextField
                            label="Value"
                            value={condition.value}
                            onChange={(value) => updateCondition(index, { value })}
                            placeholder="Value"
                            autoComplete="off"
                          />
                        )}

                        <Button onClick={() => removeCondition(index)} tone="critical">Remove</Button>
                      </InlineStack>
                    </div>
                  ))}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack gap="400" blockAlign="center" align="space-between">
                <Text variant="headingMd" as="h3">
                  <InlineStack gap="200" blockAlign="center">
                    <span style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      height: '32px',
                      width: '32px',
                      borderRadius: '50%',
                      background: '#dcfce7',
                      color: '#16a34a',
                      fontWeight: 'bold'
                    }}>3</span>
                    Then (Actions)
                  </InlineStack>
                </Text>
                <Select
                  label=""
                  options={[
                    { label: 'Add Action', value: '', disabled: true },
                    ...ACTION_TYPES.map((action) => ({ label: action.label, value: action.value }))
                  ]}
                  onChange={(value) => {
                    if (value) {
                      addAction(value as ActionType)
                    }
                  }}
                />
              </InlineStack>

              {actions.length === 0 ? (
                <Text variant="bodySm" as="p" tone="subdued" alignment="center">
                  No actions - add at least one action to complete the workflow
                </Text>
              ) : (
                <BlockStack gap="300">
                  {actions.map((action, index) => (
                    <div key={index} style={{ padding: '1rem', border: '2px solid #e5e7eb', borderRadius: '8px' }}>
                      <InlineStack gap="200" blockAlign="center" align="space-between">
                        <InlineStack gap="200" blockAlign="center">
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '24px',
                            width: '24px',
                            borderRadius: '50%',
                            background: '#dcfce7',
                            color: '#16a34a',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>{index + 1}</span>
                          <Text variant="bodySm" fontWeight="bold" as="span">
                            {ACTION_TYPES.find((t) => t.value === action.type)?.label || action.type}
                          </Text>
                        </InlineStack>
                        <InlineStack gap="100">
                          <Button onClick={() => moveAction(index, 'up')} disabled={index === 0} size="slim">↑</Button>
                          <Button onClick={() => moveAction(index, 'down')} disabled={index === actions.length - 1} size="slim">↓</Button>
                          <Button onClick={() => removeAction(index)} tone="critical" size="slim">Remove</Button>
                        </InlineStack>
                      </InlineStack>

                      <BlockStack gap="300">
                        {action.type === 'SEND_MESSAGE' && (
                          <TextField
                            label="Message"
                            value={action.message || ''}
                            onChange={(value) => updateAction(index, { message: value })}
                            multiline={3}
                            placeholder="Message text (use {{variable}} for dynamic values)"
                            autoComplete="off"
                          />
                        )}

                        {action.type === 'SEND_TEMPLATE' && (
                          <Select
                            label="Template"
                            value={action.templateId || ''}
                            onChange={(value) => updateAction(index, { templateId: value })}
                            options={[
                              { label: 'Select template...', value: '' },
                              ...templates.map((template) => ({ label: template.name, value: template.id }))
                            ]}
                          />
                        )}

                        {action.type === 'ASSIGN_AGENT' && (
                          <Select
                            label="Agent"
                            value={action.agentId || ''}
                            onChange={(value) => updateAction(index, { agentId: value })}
                            options={[
                              { label: 'Select agent...', value: '' },
                              ...agents.map((agent) => ({ label: agent.name, value: agent.id }))
                            ]}
                          />
                        )}

                        {(action.type === 'ADD_TAG' || action.type === 'REMOVE_TAG') && (
                          <TextField
                            label="Tag"
                            value={action.tag || ''}
                            onChange={(value) => updateAction(index, { tag: value })}
                            placeholder="Tag name"
                            autoComplete="off"
                          />
                        )}

                        {action.type === 'NOTIFY_AGENT' && (
                          <>
                            <Select
                              label="Agent"
                              value={action.agentId || ''}
                              onChange={(value) => updateAction(index, { agentId: value })}
                              options={[
                                { label: 'Select agent...', value: '' },
                                ...agents.map((agent) => ({ label: agent.name, value: agent.id }))
                              ]}
                            />
                            <TextField
                              label="Message"
                              value={action.message || ''}
                              onChange={(value) => updateAction(index, { message: value })}
                              placeholder="Notification message"
                              autoComplete="off"
                            />
                          </>
                        )}

                        {action.type === 'WAIT' && (
                          <InlineStack gap="200" blockAlign="center">
                            <TextField
                              label="Duration"
                              value={(action.duration || 60).toString()}
                              onChange={(value) => updateAction(index, { duration: parseInt(value) || 60 })}
                              type="number"
                              min={1}
                              autoComplete="off"
                            />
                            <Text variant="bodySm" as="p" tone="subdued">seconds</Text>
                          </InlineStack>
                        )}
                      </BlockStack>
                    </div>
                  ))}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <InlineStack gap="300" align="end">
            <Button onClick={() => navigate('/workflows')}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={loading}>
              {id && id !== 'new' ? 'Update Workflow' : 'Create Workflow'}
            </Button>
          </InlineStack>
        </Layout.Section>
      </Layout>
    </Page>
  )
}
